import { Link } from 'react-router-dom';
import { Col, Container, Row } from 'react-bootstrap';
import type { IconType } from 'react-icons';
import {
  LuArrowRight,
  LuBrain,
  LuBuilding2,
  LuCalendar,
  LuChartLine,
  LuCircleDollarSign,
  LuCode,
  LuCog,
  LuTrendingUp,
  LuUser,
  LuUsers,
  LuUsersRound,
  LuZap,
} from 'react-icons/lu';

import GradientCTA from '../app/components/GradientCTA';
import HeroAnimated from '../app/components/HeroAnimated';
import ImpactResultsSection from '../app/components/ImpactResultsSection';
import IndustriesWeServeBand from '../app/components/IndustriesWeServeBand';
import RevealSection from '../app/components/RevealSection';
import RoleLinkCard from '../app/components/RoleLinkCard';
import RolesWeFillCandidateCarousel from '../app/components/RolesWeFillCandidateCarousel';
import SbrIcon from '../app/components/SbrIcon';
import { SBR_IMPACT_STATS } from '../data/sbrImpactStats';

type CategoryTone = 'teal' | 'blue' | 'purple';

const heroFeatures: { title: string; body: string; Icon: IconType }[] = [
  {
    title: 'Pre-vetted, high-caliber talent',
    body: 'Every candidate is thoroughly screened and validated.',
    Icon: LuUsers,
  },
  {
    title: 'Faster hiring. Lower cost.',
    body: 'Reduce time-to-hire and total cost per hire—without sacrificing quality.',
    Icon: LuZap,
  },
];

const categories: { title: string; desc: string; Icon: IconType; tone: CategoryTone }[] = [
  {
    title: 'Finance & Accounting',
    desc: 'Drive financial strategy, reporting, and growth.',
    Icon: LuChartLine,
    tone: 'teal',
  },
  {
    title: 'Technology & Engineering',
    desc: 'Build, scale, and secure modern systems.',
    Icon: LuCode,
    tone: 'blue',
  },
  {
    title: 'Operations & Supply Chain',
    desc: 'Optimize processes and deliver operational excellence.',
    Icon: LuCog,
    tone: 'purple',
  },
  {
    title: 'Sales & Revenue',
    desc: 'Accelerate growth with top-performing revenue talent.',
    Icon: LuUsers,
    tone: 'teal',
  },
  {
    title: 'Customer Experience',
    desc: 'Elevate customer satisfaction and retention.',
    Icon: LuUser,
    tone: 'blue',
  },
  {
    title: 'Human Resources & People Ops',
    desc: 'Build strong teams and future-ready cultures.',
    Icon: LuUsersRound,
    tone: 'purple',
  },
];

const trendingRoles: { role: string; cat: string; Icon: IconType; tone: CategoryTone }[] = [
  { role: 'VP Sales', cat: 'Sales & Revenue', Icon: LuTrendingUp, tone: 'teal' },
  {
    role: 'Director Engineering',
    cat: '(Construction or E&C)',
    Icon: LuBuilding2,
    tone: 'blue',
  },
  {
    role: 'Senior Project Manager',
    cat: '(Construction or E&C)',
    Icon: LuCog,
    tone: 'purple',
  },
  {
    role: 'Director of Software Engineering',
    cat: '(Data Products)',
    Icon: LuCode,
    tone: 'teal',
  },
  {
    role: 'VP Product Management',
    cat: '(Data Products)',
    Icon: LuChartLine,
    tone: 'blue',
  },
  {
    role: 'Forward Deployed Engineer',
    cat: '(AI)',
    Icon: LuBrain,
    tone: 'purple',
  },
  {
    role: 'VP AI Transformation Program',
    cat: '(AI)',
    Icon: LuZap,
    tone: 'teal',
  },
  { role: 'VP Controller', cat: '(AI)', Icon: LuCircleDollarSign, tone: 'blue' },
];

export default function RolesWeFillPage() {
  return (
    <>
      <section className="rwf-hero hero-pattern text-white">
        <div className="rwf-hero__main">
          <Container className="rwf-hero__container">
            <Row className="rwf-hero__row align-items-stretch gy-4">
              <Col lg={6} className="rwf-hero__copy-col d-flex flex-column">
                <HeroAnimated className="rwf-hero__animated">
                  <div className="rwf-hero__headline">
                    <p data-hero-item className="rwf-hero-eyebrow mb-0">
                      Roles We Fill
                    </p>
                    <h1 data-hero-item className="display-5 fw-bold mb-0">
                      Specialized talent for{' '}
                      <span className="text-gradient-teal-blue">high-impact</span> outcomes.
                    </h1>
                  </div>

                  <div className="rwf-hero__visual rwf-hero__visual--mobile d-lg-none">
                    <RolesWeFillCandidateCarousel />
                  </div>

                  <div className="rwf-hero__body">
                    <p data-hero-item className="lead text-white-50 mb-0 pe-lg-2">
                      We fill mission-critical, hard-to-find roles that drive growth, efficiency, and
                      competitive advantage.
                    </p>
                  </div>

                  <div data-hero-item className="rwf-hero-features mt-auto">
                    {heroFeatures.map((f) => (
                      <div key={f.title} className="solution-hero-feature flex-grow-1 min-w-0">
                        <div className="solution-hero-feature__icon">
                          <SbrIcon icon={f.Icon} size={22} />
                        </div>
                        <div className="solution-hero-feature__copy">
                          <div className="solution-hero-feature__title">{f.title}</div>
                          <p className="solution-hero-feature__body">{f.body}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </HeroAnimated>
              </Col>

              <Col lg={6} className="rwf-hero__visual-col d-none d-lg-flex">
                <RevealSection className="h-100 d-flex align-items-center w-100">
                  <RolesWeFillCandidateCarousel />
                </RevealSection>
              </Col>
            </Row>
          </Container>
        </div>
      </section>

      <section className="bg-sbr-light py-5 py-lg-6">
        <RevealSection>
          <Container>
            <div className="rwf-categories-header text-center">
              <h2 className="sbr-section__title fw-bold mb-0">Role Categories (by Function)</h2>
            </div>
            <div className="rwf-categories__track">
              {categories.map((c) => (
                <article key={c.title} className={`rwf-category-card rwf-category-card--${c.tone}`}>
                  <div className="rwf-category-card__icon" aria-hidden>
                    <SbrIcon icon={c.Icon} size={24} />
                  </div>
                  <h3 className="rwf-category-card__title">{c.title}</h3>
                  <p className="rwf-category-card__desc">{c.desc}</p>
                  <Link className="rwf-category-card__link" to="/book-a-call">
                    View Roles
                    <SbrIcon icon={LuArrowRight} size={16} className="ms-1" aria-hidden />
                  </Link>
                </article>
              ))}
            </div>
          </Container>
        </RevealSection>
      </section>

      <IndustriesWeServeBand variant="dark" />

      <section className="bg-sbr-light py-5 py-lg-6">
        <RevealSection>
          <Container>
            <div className="rwf-trending">
              <div className="rwf-trending__header text-center">
                <h2 className="sbr-section__title fw-bold mb-2">Trending Roles We Fill</h2>
                <p className="rwf-trending__subtitle mb-0">
                  High-demand, hard-to-fill roles companies trust us to deliver.
                </p>
              </div>
              <div className="rwf-trending__grid">
                {trendingRoles.map((t) => (
                  <RoleLinkCard
                    key={t.role}
                    item={{ title: t.role, subtitle: t.cat, Icon: t.Icon }}
                    to="/book-a-call"
                  />
                ))}
              </div>
              <div className="rwf-trending__footer">
                <Link className="rwf-trending__view-all" to="/book-a-call">
                  View All Roles We Fill
                  <SbrIcon icon={LuArrowRight} size={18} className="ms-1" aria-hidden />
                </Link>
              </div>
            </div>
          </Container>
        </RevealSection>
      </section>

      <ImpactResultsSection
        reveal
        className="rwf-results-impact"
        eyebrow="Results That Matter"
        title={
          <>
            <span className="rwf-results-impact__title-line">The Right Role.</span>
            <span className="rwf-results-impact__title-line rwf-results-impact__title-line--person">
              The Right Person.
            </span>
            <span className="rwf-results-impact__title-line text-gradient-teal-blue">Faster.</span>
          </>
        }
        lead="We help companies hire smarter, move faster, and build stronger teams."
        cta={{ to: '/book-a-call', label: 'Book a 30 Min Call' }}
        stats={SBR_IMPACT_STATS}
      />

      <GradientCTA
        icon={<SbrIcon icon={LuCalendar} size={26} />}
        title={<>Need a role filled this quarter? Let&apos;s pressure-test feasibility — and execution speed.</>}
      />
    </>
  );
}
